import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimation';
import SEO from '../components/SEO';

interface FormState {
  name: string;
  business_name: string;
  email: string;
  phone: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  business_name: '',
  email: '',
  phone: '',
  message: '',
};

export default function Contact() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const containerRef = useScrollAnimations();

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please complete all required fields.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new URLSearchParams({
        'form-name': 'contact',
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });

      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (!res.ok) throw new Error('Submission failed');

      setSubmitted(true);
    } catch {
      setError('There was a problem submitting your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div ref={containerRef}>
      <SEO 
        title="Contact Us | 7th Vision" 
        description="Get in touch with 7th Vision to discuss your business operations, communication challenges, or website needs."
      />
      {/* Hero */}
      <section className="relative bg-stone-900 pt-28 sm:pt-40 pb-14 sm:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 to-stone-900" />
        <div className="relative section-padding">
          <p className="section-label text-bronze-400 mb-4">Contact</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-100 leading-tight max-w-3xl mb-6">
            Let's begin with a conversation.
          </h1>
          <p className="text-stone-400 text-lg font-light max-w-lg leading-relaxed">
            Tell us a little about your business and what you're working through. We will
            be in touch to arrange a time to speak.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="section-padding py-14 md:py-32 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-20">
            {/* Left info */}
            <div className="lg:col-span-2 fade-up">
              <p className="section-label mb-6">Before We Talk</p>
              <div className="space-y-8">
                {[
                  {
                    heading: 'No generic proposals',
                    body: 'We do not send pre-packaged offers. Every conversation begins with listening, not pitching.',
                  },
                  {
                    heading: 'No obligation',
                    body: 'An initial conversation is simply that: a conversation. We will only recommend a path forward if we believe it is genuinely right for your business.',
                  },
                  {
                    heading: 'We respond thoughtfully',
                    body: 'You will hear back from us within one business day. We take the time to read what you have shared before making contact.',
                  },
                ].map((item, i) => (
                  <div key={i} className="fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                    <div className="w-8 h-px bg-bronze-400 mb-4" />
                    <h3 className="font-serif text-lg text-stone-800 mb-2">{item.heading}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 fade-up" style={{ transitionDelay: '150ms' }}>
              {submitted ? (
                <div className="flex flex-col items-start justify-center h-full py-12">
                  <CheckCircle size={40} className="text-bronze-500 mb-6" />
                  <h2 className="font-serif text-3xl text-stone-800 mb-4">
                    Thank you for reaching out.
                  </h2>
                  <div className="w-12 h-px bg-bronze-400 mb-6" />
                  <p className="text-stone-500 leading-relaxed max-w-md">
                    We have received your message and will be in touch within one business
                    day. We appreciate you taking the time to share what you're working
                    through.
                  </p>
                </div>
              ) : (
                <form
                  name="contact"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  noValidate
                  data-netlify="true"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-stone-500 mb-2">
                        Your Name <span className="text-bronze-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800 transition-colors duration-300"
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-stone-500 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        name="business_name"
                        value={form.business_name}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800 transition-colors duration-300"
                        placeholder="Your business"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-stone-500 mb-2">
                        Email Address <span className="text-bronze-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800 transition-colors duration-300"
                        placeholder="you@yourbusiness.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-stone-500 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800 transition-colors duration-300"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-widest uppercase text-stone-500 mb-2">
                      What are you working through? <span className="text-bronze-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={6}
                      className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800 transition-colors duration-300 resize-none"
                      placeholder="Describe your current situation or the challenges you are facing. The more context you share, the more useful our initial conversation will be."
                      required
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quiet Quote */}
      <section className="section-padding py-12 md:py-24 bg-stone-800">
        <div className="max-w-3xl mx-auto text-center fade-up">
          <div className="w-12 h-px bg-bronze-500 mx-auto mb-10" />
          <p className="font-serif text-2xl md:text-3xl text-stone-300 leading-relaxed italic font-light">
            "A business that runs well behind the scenes always shows in the experience it
            delivers to customers."
          </p>
          <div className="w-12 h-px bg-bronze-500 mx-auto mt-10" />
        </div>
      </section>
    </div>
  );
}
